import BigNumber from 'bignumber.js';
import _ from 'lodash';

import { E償却方法, E勘定科目カテゴリ, E税区分, E製造原価科目未使用フラグ, Eフェーズ種別 } from '@/openapi/enum';
import { データListItemResponse, Cデータ償却, YearValue } from '@/openapi/user';

import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import {
  償却FunctionType,
  償却FunctionTypeKeys,
  StaticColumnConfig,
  YearMonth,
  償却,
  償却EditItem,
  償却ListItem,
  科目別償却合計,
  自動計算償却,
  計算減価償却,
  販管製造,
  項目別Values,
  科目別期末簿価,
} from '@/classes/service/分析計画/償却Type';
import { m償却率, 計算用償却率 } from '@/master/償却';
import AppFormatUtility from '@/classes/common/AppFormatUtility';
import 決算期Utility from '@/classes/common/決算期Utility';

/** const */
// TODO: 見直し
export const 科目別償却合計Keys = ['前期末簿価', '再計算前期末簿価', '償却不足額'] as const;

const CALC_CONST = {
  一括償却資産分割回数: 3,
  残存価額: {
    有形: 1,
    無形: 0,
  },
};

export default class 償却Service extends AbstractデータService<償却> {
  /** override */
  protected serviceType = ServiceType.償却;

  /** c計画0期の決算期の年と月 */
  private c計画0期の決算期Ym: YearMonth;

  /** 償却決算年to (分析フェーズ：計画0期の決算期の前期、計画フェーズ：計画0期の決算期から計画作成期間分) */
  private 償却決算年to: number;

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別.分析フェーズ | Eフェーズ種別.計画フェーズ | null = null) {
    super(eフェーズ種別);

    // クラス変数の取得
    this.setBnRoundingMode();
    this.c計画0期の決算期Ym = this.getYm(AbstractデータService.計画書.c計画0期の決算期);

    this.償却決算年to =
      this.eフェーズ種別 === Eフェーズ種別.分析フェーズ
        ? this.c計画0期の決算期Ym.year - 1
        : this.c計画0期の決算期Ym.year + AbstractデータService.事業所.c計画作成期間;
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse(データs: データListItemResponse[], option?: Record<string, string | number>): 償却 {
    // 機能種別の取得
    const functionType = this.getFunctionType(option!.functionType);
    // 必要データに絞り込んで計算可能データ化
    let filteredデータs = this.入力分類Filter(データs);
    // 設備投資の時は計画のデータのみ、それ以外は分析と計画のデータに絞る
    filteredデータs = filteredデータs.filter((item) => {
      return functionType === '設備投資'
        ? item.cフェーズ種別 === Eフェーズ種別.計画フェーズ
        : item.cフェーズ種別 === Eフェーズ種別.分析フェーズ || item.cフェーズ種別 === Eフェーズ種別.計画フェーズ;
    });

    // cデータを展開する
    const listItems: 償却EditItem[] = filteredデータs.map((d) => {
      const cデータ償却 = JSON.parse(d.cデータ) as Cデータ償却;
      return {
        データpk: d.pk,
        番号: d.c番号,
        フェーズ種別: d.cフェーズ種別,
        ...cデータ償却,
      };
    });

    // eフェーズ種別により、償却決算年のfromを取得
    let v償却決算年from: number;
    if (this.eフェーズ種別 === Eフェーズ種別.分析フェーズ) {
      // 分析フェーズ from：最も古い事業供用日から取得
      if (listItems.length === 0) {
        v償却決算年from = this.償却決算年to;
      } else {
        const minYm = this.getYm(
          Math.min(...listItems.map((item) => AppFormatUtility.parse年月Number(item.事業供用日)))
        );
        v償却決算年from = this.getYearBy決算月(minYm);
      }
    } else {
      // 計画フェーズ from：計画0期の決算期
      v償却決算年from = this.c計画0期の決算期Ym.year;
    }

    // 償却決算年の配列を作成
    const visible決算年s: number[] = [];
    for (let i = v償却決算年from; i <= this.償却決算年to; i++) {
      visible決算年s.push(i);
    }

    // ヘッダ作成
    const 決算期Labels = 決算期Utility.get決算期Labels(
      visible決算年s,
      this.c計画0期の決算期Ym.month,
      this.eフェーズ種別 === Eフェーズ種別.計画フェーズ,
      false
    );
    const header = visible決算年s.map((year, index) => ({
      year,
      label: 決算期Labels[index],
    }));

    // 償却予測額で返却する販管製造sを取得するための変数を初期化
    const initial販管費割合 = 100;
    const initial部門Pk = this.部門s.length > 0 ? this.部門s[0].pk : 0;

    // 一覧作成
    const list = listItems.map((item) => {
      // 減価償却費の自動計算
      const v自動計算減価償却 = this.create減価償却s(item);

      // 一覧作成用減価償却s（自動計算した減価償却費にユーザーが入力した減価償却費を上書きしたものを入れる想定）
      const 一覧作成用減価償却s = v自動計算減価償却.減価償却s;

      // DBに保存されている減価償却費を、一覧作成用にマージする
      item.減価償却s?.forEach((v保存減価償却) => {
        // 分析フェーズで作成したデータは、償却予測額の画面ではDBの減価償却費を使用しない
        if (functionType === '償却予測額' && item.フェーズ種別 === Eフェーズ種別.分析フェーズ) return;

        const v一覧作成用減価償却 = 一覧作成用減価償却s.find((item) => item.year === v保存減価償却.year);
        if (v一覧作成用減価償却 === undefined) {
          一覧作成用減価償却s.push(v保存減価償却);
        } else {
          v一覧作成用減価償却.value = v保存減価償却.value ?? 0;
        }
      });

      // 変数初期化
      let v計算用前期末簿価 = this.replace期末簿価if計画0期前期(
        一覧作成用減価償却s.length > 0 ? 一覧作成用減価償却s[0].year - 1 : 0,
        item.取得価額,
        item.前期末簿価
      );
      if (functionType === '償却予測額') {
        // 販管費割合がなければ初期値を設定
        if (item.販管費割合 === undefined) item.販管費割合 = initial販管費割合;
      }
      const 年次計算減価償却Map: Map<number, 計算減価償却> = new Map();

      // 期末簿価・償却累計額を計算しながら、一覧作成用減価償却sを計算減価償却Mapに詰め替え
      一覧作成用減価償却s.forEach((d) => {
        v計算用前期末簿価 = this.replace期末簿価if計画0期前期(d.year, v計算用前期末簿価 - d.value, item.前期末簿価);
        年次計算減価償却Map.set(d.year, {
          減価償却費: d.value,
          期末簿価: v計算用前期末簿価,
          償却累計額: item.取得価額 - v計算用前期末簿価,
        });
      });

      // 詰め替え終わったら減価償却sをなくす
      delete item.減価償却s;

      // 勘定科目pk から科目名を取得
      const 科目名 = this.勘定科目s.find((勘) => 勘.pk === item.勘定科目Pk)?.c勘定科目 ?? '';

      // 一覧行
      const responseListItem: 償却ListItem = {
        ...item,
        科目名,
        償却率: this.get計算用償却率(item)?.償却率,
        定率法償却: v自動計算減価償却.定率法償却,
        年次計算減価償却Map,
      };

      // 各画面に必要な値を追加
      switch (functionType) {
        case '償却不足額':
          // 再計算前期末簿価・償却不足額を取得
          responseListItem.再計算前期末簿価 =
            年次計算減価償却Map.get(visible決算年s[visible決算年s.length - 1])?.期末簿価 ?? 0;
          responseListItem.償却不足額 = (item.前期末簿価 ?? 0) - responseListItem.再計算前期末簿価;
          break;
        case '償却予測額':
          // 部門がなければ初期値(一番小さい部門コードを持つ部門)を設定
          if (item.部門Pk === undefined) responseListItem.部門Pk = initial部門Pk;
          // 部門Pkから部門名をセット
          responseListItem.部門名 = this.部門s.find((部) => 部.pk === responseListItem.部門Pk)?.c部門名 ?? '';
          break;
      }

      return responseListItem;
    });

    // 合計作成
    // 指定配列の計算減価償却から合計を取得するローカル関数
    const getSumBy計算減価償却 = (
      list: { 年次計算減価償却Map: Map<number, 計算減価償却> }[],
      key: keyof 計算減価償却
    ): number[] => {
      return visible決算年s.map((year) => _.sumBy(list, (item) => item.年次計算減価償却Map.get(year)?.[key] ?? 0));
    };

    // 各期の減価償却費の合計
    const 減価償却s = getSumBy計算減価償却(list, '減価償却費');

    // 画面用返却データ
    const response: 償却 = {
      header,
      list,
      total: {
        減価償却s,
      },
    };

    // 各画面で必要な合計値を追加
    switch (functionType) {
      case '償却不足額':
        const sum科目別償却 = (listItems: 科目別償却合計[]): 科目別償却合計 => {
          const v科目別償却合計: 科目別償却合計 = {};
          listItems.forEach((listItem) => {
            科目別償却合計Keys.forEach((科目別償却合計Key) => {
              v科目別償却合計[科目別償却合計Key] =
                (v科目別償却合計[科目別償却合計Key] ?? 0) + (listItem[科目別償却合計Key] ?? 0);
            });
          });
          return v科目別償却合計;
        };

        const 償却s = Object.values(_.groupBy(list, '科目名')).map((科目別list) => {
          const v科目別償却合計 = sum科目別償却(科目別list);
          return {
            科目名: 科目別list[0].科目名,
            ...v科目別償却合計,
          };
        });

        response.total.償却不足額 = {
          償却不足額: _.sumBy(list, '償却不足額'),
          科目別集計: {
            償却s,
            合計: sum科目別償却(償却s),
          },
        };
        break;

      case '設備投資':
        // 指定の税区分を持つ勘定科目Pkを取得する関数
        const get勘定科目PkBy税区分 = (e税区分: E税区分) => {
          return this.勘定科目s
            .filter(
              (勘) =>
                (勘.c中分類 === '有形固定資産' || 勘.c中分類 === '無形固定資産') &&
                勘.cカテゴリ === E勘定科目カテゴリ.貸借 &&
                勘.c税区分 === e税区分
            )
            .map((勘) => 勘.pk);
        };
        const 課税仕入Pks = get勘定科目PkBy税区分(E税区分.課税仕入);
        const 課税仕入返還Pks = get勘定科目PkBy税区分(E税区分.課税仕入返還);

        // listItems内での、年毎の取得価額合計を返す関数
        const create取得価額values = (listItems: 償却ListItem[]): number[] => {
          return visible決算年s.map((year) => {
            // 事業供用日が対象年のデータに絞り、取得価額を合算する
            const filteredItems = listItems.filter(
              (item) => year === this.getYearBy決算月(this.getYm(AppFormatUtility.parse年月Number(item.事業供用日)))
            );
            return _.sumBy(filteredItems, '取得価額');
          });
        };

        // 返却データ作成
        const 取得価額s: 項目別Values[] = Object.values(_.groupBy(list, '科目名')).map((科目別List) => {
          const values = create取得価額values(科目別List);
          return {
            項目名: 科目別List[0].科目名!,
            values,
          };
        });
        const 課税仕入s = create取得価額values(list.filter((item) => 課税仕入Pks.includes(item.勘定科目Pk!)));
        const 課税仕入返還s = create取得価額values(list.filter((item) => 課税仕入返還Pks.includes(item.勘定科目Pk!)));

        response.total.設備投資 = {
          科目別集計: {
            取得価額s,
            合計: visible決算年s.map((_year, index) => _.sumBy(取得価額s, (item) => item.values[index] ?? 0)),
          },
          税区分別集計: {
            課税仕入s,
            課税仕入返還s,
          },
        };
        break;
      case '償却予測額':
        const 部門別減価償却s: 項目別Values[] = Object.values(_.groupBy(list, '部門名')).map((部門別List) => {
          return {
            項目名: 部門別List[0].部門名!,
            values: getSumBy計算減価償却(部門別List, '減価償却費'),
          };
        });

        const 科目別期末簿価s: 科目別期末簿価[] = Object.values(_.groupBy(list, '勘定科目Pk')).map((科目別List) => {
          return {
            勘定科目Pk: 科目別List[0].勘定科目Pk!,
            項目名: 科目別List[0].科目名,
            values: getSumBy計算減価償却(科目別List, '期末簿価'),
            減価償却s: getSumBy計算減価償却(科目別List, '減価償却費'),
          };
        });

        // 販管製造の集計
        // 販管製造配列を合計する関数
        const getSumBy販管製造 = (list: { 販管製造s: 販管製造[] }[]): 販管製造[] => {
          if (list.length === 0) return [];
          return visible決算年s.map((_y, i) => {
            return {
              販管: _.sumBy(list, (item) => item.販管製造s[i].販管),
              製造: _.sumBy(list, (item) => item.販管製造s[i].製造),
            };
          });
        };

        // 集計用のリストを作成
        const 販管製造計算用list: {
          部門Pk: number;
          販管製造s: 販管製造[];
        }[] = list.map((listItem) => {
          const v販管製造s: 販管製造[] = visible決算年s.map((year) => {
            const v減価償却費 = listItem.年次計算減価償却Map.get(year)?.減価償却費 ?? 0;
            const v販管費 = this.calcPercentByBigNumberWith減価償却費端数処理(v減価償却費, listItem.販管費割合!, 100);
            const v製造費 = v減価償却費 - v販管費;
            return {
              販管: v販管費,
              製造: v製造費,
            };
          });
          return {
            部門Pk: listItem.部門Pk!,
            販管製造s: v販管製造s,
          };
        });

        // 部門別集計
        const 部門別販管製造s: {
          部門Pk: number;
          販管製造s: 販管製造[];
        }[] = Object.values(_.groupBy(販管製造計算用list, '部門Pk')).map((部門別List) => {
          return {
            部門Pk: 部門別List[0].部門Pk,
            販管製造s: getSumBy販管製造(部門別List),
          };
        });
        // 全体集計
        const 販管製造s = getSumBy販管製造(部門別販管製造s);

        response.total.償却予測額 = {
          販管製造s,
          部門別減価償却s,
          科目別期末簿価s,
          部門別販管製造s,
        };
        break;
    }

    // 償却データ返却
    return response;
  }

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, string | number>
  ): 帳票データ {
    const raw = this.parse(データs, option);

    // テンプレート行設定
    const templateConfig = {
      list: {
        header: 0,
        row: {
          normal: [1, 2, 3],
          fixedRate: [4, 5, 6],
          lease: [7, 8, 9],
        },
        total: 10,
      },
      simpleList: {
        row: [1, 2],
        total: 3,
      },
      償却不足額: {
        科目別集計: {
          header: 11,
          row: 12,
          total: 13,
        },
      },
      設備投資: {
        科目別集計: {
          row: 11,
          total: 12,
        },
      },
      償却予測額: {
        項目別集計: {
          row: 11,
        },
      },
    };

    // 静的カラム数設定
    const staticColumnConfigs: StaticColumnConfig[] = [
      {
        functionType: '償却不足額',
        isSimple: false,
        size: {
          start: 14,
          end: 3,
        },
      },
      {
        functionType: '設備投資',
        isSimple: false,
        size: {
          start: 13,
          end: 0,
        },
      },
      {
        functionType: '償却予測額',
        isSimple: false,
        size: {
          start: 15,
          end: 0,
        },
      },
      {
        functionType: '償却予測額',
        isSimple: true,
        size: {
          start: 2,
          end: 0,
        },
      },
    ];

    // 機能種別、使用パラメタの取得
    const opt = option!;
    const functionType = this.getFunctionType(opt.functionType);
    const isSimple = (opt.isSimple as unknown as boolean) ?? false;
    const is製造販管 = AbstractデータService.事業所.c製造原価科目未使用フラグ === E製造原価科目未使用フラグ.使用する;
    const staticColumnCount = staticColumnConfigs.find(
      (c) => c.functionType === functionType && c.isSimple === isSimple
    )!.size;

    // 以下帳票データ整形
    const dynamicColumnCount = raw.header.length;
    const staticEndColumnNo = staticColumnCount.start + 1;
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfig.list.header];
    const columns = [
      ..._.range(staticColumnCount.start),
      ...new Array(dynamicColumnCount).fill(staticColumnCount.start),
      ..._.range(staticEndColumnNo, staticEndColumnNo + staticColumnCount.end),
    ];
    // 償却予測額で製造原価科目.使用しないの場合は列数を減らす
    if (functionType === '償却予測額' && !is製造販管 && !isSimple) {
      const 製造販管ColumnNo = 13;
      columns.splice(columns.indexOf(製造販管ColumnNo), 1);
      staticColumnCount.start -= 1;
    }
    const dynamicConfigs = [
      {
        columns,
      },
    ];
    const values: unknown[][] = [
      [
        ...this.templateCellValues(staticColumnCount.start),
        ...raw.header.map((h) => h.label),
        ...this.templateCellValues(staticColumnCount.end),
      ],
    ];

    // 一覧整形
    if (isSimple) {
      // 簡易表示（償却予測額）
      raw.total.償却予測額!.科目別期末簿価s.forEach((listItem) => {
        templateRows.push(...templateConfig.simpleList.row);
        // 1行目
        values.push([listItem.項目名, null, ...listItem.減価償却s.map((val) => this.convertByMoneyUnit(val))]);
        // 2行目
        values.push([...this.templateCellValues(2), ...listItem.values.map((val) => this.convertByMoneyUnit(val))]);
      });
    } else {
      // 標準表示
      raw.list.forEach((listItem) => {
        // 使用するテンプレート行を設定
        let rowConfig;
        switch (listItem.償却方法) {
          case E償却方法.定率法200pct:
          case E償却方法.定率法250pct:
            rowConfig = templateConfig.list.row.fixedRate;
            break;
          case E償却方法.リース期間定額法:
            rowConfig = templateConfig.list.row.lease;
            break;
          default:
            rowConfig = templateConfig.list.row.normal;
        }
        templateRows.push(...rowConfig);

        // 1行目
        const datas: unknown[] = [];
        // 共通部分
        datas.push(
          listItem.番号,
          listItem.資産コード,
          listItem.名称,
          ...this.templateCellValues(2),
          listItem.科目名,
          listItem.償却方法,
          listItem.数量,
          listItem.事業供用日,
          this.convertByMoneyUnit(listItem.取得価額),
          listItem.耐用年数,
          listItem.償却率
        );
        // 機能種別により挿入
        switch (functionType) {
          case '償却不足額':
            datas.push(this.convertByMoneyUnit(listItem.前期末簿価));
            break;
          case '償却予測額':
            datas.push(listItem.部門名);
            if (is製造販管) datas.push(`${100 - listItem.販管費割合!}:${listItem.販管費割合}`);
            break;
        }
        // 共通部分
        datas.push(
          null,
          ...raw.header.map((h) => this.convertByMoneyUnit(listItem.年次計算減価償却Map.get(h.year)?.減価償却費))
        );
        // 機能種別により挿入
        if (functionType === '償却不足額') {
          datas.push(
            this.convertByMoneyUnit(listItem.再計算前期末簿価),
            this.convertByMoneyUnit(listItem.償却不足額),
            listItem.備考
          );
        }
        // 1行目追加
        values.push(datas);

        // 2行目
        const middleNullCount = staticColumnCount.start - 10;
        values.push([
          ...this.templateCellValues(6),
          listItem.定率法償却 !== undefined ? listItem.定率法償却.保証率 : listItem.リース償却?.月数 ?? null,
          ...this.templateCellValues(2),
          this.convertByMoneyUnit(listItem.定率法償却?.償却保証額),
          ...this.templateCellValues(middleNullCount),
          ...raw.header.map((h) => this.convertByMoneyUnit(listItem.年次計算減価償却Map.get(h.year)?.期末簿価)),
          ...this.templateCellValues(staticColumnCount.end),
        ]);

        // 3行目
        values.push([
          ...this.templateCellValues(6),
          listItem.定率法償却 !== undefined ? listItem.定率法償却.改定償却率 : listItem.リース償却?.残価保証額,
          ...this.templateCellValues(2),
          this.convertByMoneyUnit(listItem.定率法償却?.改定取得価額),
          ...this.templateCellValues(middleNullCount),
          ...raw.header.map((h) => this.convertByMoneyUnit(listItem.年次計算減価償却Map.get(h.year)?.償却累計額)),
          ...this.templateCellValues(staticColumnCount.end),
        ]);
      });
    }

    // 合計行
    const totalTemplateConfig = isSimple ? templateConfig.simpleList.total : templateConfig.list.total;
    // 償却予測額の時、販管製造合計追加
    // 販管製造合計を作成する関数
    const push販管製造合計 = (label: string, key: '販管' | '製造') => {
      templateRows.push(totalTemplateConfig);
      values.push([
        ...this.templateCellValues(staticColumnCount.start - 1),
        label,
        ...raw.total.償却予測額!.販管製造s.map((value) => this.convertByMoneyUnit(value[key])),
      ]);
    };
    if (functionType === '償却予測額' && is製造販管) {
      push販管製造合計('減価償却費（販管費）', '販管');
      push販管製造合計('減価償却費（製造）', '製造');
    }

    // 減価償却合計整形
    templateRows.push(totalTemplateConfig);
    const totalDatas: unknown[] = [];
    // 共通部分
    totalDatas.push(
      ...this.templateCellValues(staticColumnCount.start),
      ...raw.total.減価償却s.map((val) => this.convertByMoneyUnit(val))
    );
    // 機能種別により挿入
    if (functionType === '償却不足額') {
      totalDatas.push(null, this.convertByMoneyUnit(raw.total.償却不足額?.償却不足額), null);
    }
    // 減価償却合計追加
    values.push(totalDatas);

    // 集計表作成のためのローカル関数
    // 科目別償却合計の各値を配列で返す関数
    const get科目別合計 = (item: 科目別償却合計): (number | undefined)[] => {
      const arr: (number | undefined)[] = [];
      科目別償却合計Keys.forEach((key) => {
        arr.push(item[key] === undefined ? undefined : this.getMoneyUnitValue(item[key]));
      });
      return arr;
    };
    // 動的設定列数を含め、科目別償却合計欄右の出力なしセルのnull配列を返す関数
    const getMargin科目別償却 = (): null[] => {
      return this.templateCellValues(dynamicColumnCount + 12);
    };
    // 項目別Valuesによる項目別集計表を作成する関数
    const push項目別集計表 = (rows: 項目別Values[], templateRowNumber: number, label: string) => {
      rows.forEach((row, index) => {
        templateRows.push(templateRowNumber);
        values.push([
          ...this.templateCellValues(staticColumnCount.start - 2),
          index === 0 ? label : null,
          row.項目名,
          ...row.values.map((val) => this.convertByMoneyUnit(val)),
        ]);
      });
    };

    // 機能種別により集計表整形
    switch (functionType) {
      case '償却不足額':
        // 科目別集計整形
        templateRows.push(templateConfig.償却不足額.科目別集計.header);
        values.push([]);
        const 科目別集計 = raw.total.償却不足額!.科目別集計;
        科目別集計.償却s.forEach((item) => {
          templateRows.push(templateConfig.償却不足額.科目別集計.row);
          values.push([item.科目名, null, ...get科目別合計(item), ...getMargin科目別償却()]);
        });
        templateRows.push(templateConfig.償却不足額.科目別集計.total);
        values.push([...this.templateCellValues(2), ...get科目別合計(科目別集計.合計), ...getMargin科目別償却()]);
        break;

      case '設備投資':
        // 科目別取得価額集計整形
        push項目別集計表(raw.total.設備投資!.科目別集計.取得価額s, templateConfig.設備投資.科目別集計.row, '取得価額');
        templateRows.push(templateConfig.設備投資.科目別集計.total);
        values.push([
          ...this.templateCellValues(staticColumnCount.start - 1),
          '合計',
          ...raw.total.設備投資!.科目別集計.合計.map((val) => this.convertByMoneyUnit(val)),
        ]);
        break;

      case '償却予測額':
        if (isSimple) break;

        // 部門別減価償却集計整形
        push項目別集計表(
          raw.total.償却予測額!.部門別減価償却s,
          templateConfig.償却予測額.項目別集計.row,
          '部門別減価償却費'
        );
        push項目別集計表(
          raw.total.償却予測額!.科目別期末簿価s,
          templateConfig.償却予測額.項目別集計.row,
          '科目別期末簿価'
        );
        break;
    }

    return {
      templateRows,
      values,
      dynamicConfigs,
    };
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  public create減価償却s(item: 償却EditItem): 自動計算償却 {
    // 償却率表から計算用償却率を取得
    const v計算用償却率 = this.get計算用償却率(item);

    // 作成する減価償却s
    const 減価償却s: YearValue[] = [];

    // 減価償却費を自動計算
    // 事業供用日から減価償却を始める年を取得
    const v事業供用日Ym = this.getYm(AppFormatUtility.parse年月Number(item.事業供用日));
    const v償却開始年 = this.getYearBy決算月(v事業供用日Ym);

    // 初年度使用月数を取得
    const v初年度使用月数 =
      v事業供用日Ym.month <= this.c計画0期の決算期Ym.month
        ? this.c計画0期の決算期Ym.month - v事業供用日Ym.month + 1
        : 12 - (v事業供用日Ym.month - this.c計画0期の決算期Ym.month) + 1;

    // 変数初期化
    let v期末簿価 = item.取得価額;
    let v経過年数 = 0;
    let 定率法償却; // 作成する一覧の要素。定率法の時のみ、自動計算で算出した値をセットする。
    // 計画フェーズで前期末簿価の設定がある時は、前期末簿価を初期値とし、計画0期から計算を始める。
    if (
      this.eフェーズ種別 === Eフェーズ種別.計画フェーズ &&
      item.前期末簿価 !== undefined &&
      v償却開始年 <= this.c計画0期の決算期Ym.year
    ) {
      v期末簿価 = item.前期末簿価;
      v経過年数 = this.c計画0期の決算期Ym.year - v償却開始年;
    }

    // ローカル関数
    // 小数点を含む乗算をし、c減価償却費端数処理による端数処理をして、整数を返却する
    const multiplyAndRound = (x: number, y: number): number => {
      return this.roundBigNumberToInteger(new BigNumber(x).times(y));
    };
    // value(1年分)を月割り換算した値を返す
    const monthlyConvert = (value: number, monthCount: number): number => {
      return this.calcPercentByBigNumberWith減価償却費端数処理(value, monthCount, 12);
    };
    // 初年度の場合は月割り換算した減価償却費を返却する
    const get初年度換算減価償却費 = (value: number): number => {
      return v経過年数 === 0 ? monthlyConvert(value, v初年度使用月数) : value;
    };
    // 減価償却sに要素を追加し、v期末簿価を減算、v経過年数を1年進める関数
    const push減価償却AndReduce期末簿価 = (value: number): void => {
      const year = v償却開始年 + v経過年数;
      減価償却s.push({
        year,
        value,
      });
      v期末簿価 = this.replace期末簿価if計画0期前期(year, v期末簿価 - value, item.前期末簿価);
      v経過年数++;
    };
    // 残存価額まで償却する関数
    const push残存価額 = (v残存価額: number): void => {
      if (v期末簿価 > v残存価額) {
        push減価償却AndReduce期末簿価(v期末簿価 - v残存価額);
      }
    };

    // 償却方法により、減価償却費を算出
    switch (item.償却方法) {
      case E償却方法.定額法:
      case E償却方法.定額法無形固定資産:
        // 償却率が取得できない場合は計算しない
        if (v計算用償却率 === undefined) break;

        // 減価償却費＝取得価額×定額法償却率
        // ※定額法：残存価額 1円、定額法(無形固定資産)：残存価額 0円
        const v定額法減価償却費 = multiplyAndRound(item.取得価額, v計算用償却率.償却率);
        while (v定額法減価償却費 > 0 && v定額法減価償却費 < v期末簿価) {
          push減価償却AndReduce期末簿価(get初年度換算減価償却費(v定額法減価償却費));
        }
        push残存価額(item.償却方法 === E償却方法.定額法 ? CALC_CONST.残存価額.有形 : CALC_CONST.残存価額.無形);
        break;

      case E償却方法.旧定額法:
      case E償却方法.旧定率法:
        // 償却率が取得できない場合は計算しない
        if (v計算用償却率 === undefined) break;

        // （ア）償却累計額が取得価額×95%になるまで
        // 　　(a)　耐用年数経過まで
        // 　　　　　減価償却費 ＝ 算出基準値 × 旧定額法償却率
        // 　　(b)　耐用年数経過後
        // 　　　　　減価償却費 ＝ 算出基準値 × 旧定額法償却率　と　前期末簿価 －「取得価額×5%」 の少ない方
        // ※算出基準値：【旧定額法】取得価額 × 90%　【旧定率法】取得価額 － 償却累計額

        const v取得価額90pct = multiplyAndRound(item.取得価額, 0.9);
        const v取得価額5pct = multiplyAndRound(item.取得価額, 0.05);
        const v耐用月数 = item.耐用年数! * 12;

        let v償却済月数 = v経過年数 > 0 ? (v経過年数 - 1) * 12 + v初年度使用月数 : 0;
        // 償却累計額が取得価額×95% (= 期末簿価が取得価額×5%) になるまで
        while (v期末簿価 > v取得価額5pct) {
          // 償却方法により減価償却費を算出
          const v算出減価償却費 = multiplyAndRound(
            item.償却方法 === E償却方法.旧定額法 ? v取得価額90pct : v期末簿価,
            v計算用償却率.償却率
          );
          // 「v期末簿価 - 取得価額5pct」と比べて小さい方を返す関数
          const getMinCompareTo5pct = (value: number): number => {
            return Math.min(value, v期末簿価 - v取得価額5pct);
          };

          let before95pct減価償却費 = 0;
          if (v償却済月数 + 12 <= v耐用月数) {
            // 耐用年数経過前
            const v償却月数 = v経過年数 === 0 ? v初年度使用月数 : 12;
            before95pct減価償却費 = monthlyConvert(v算出減価償却費, v償却月数);
            v償却済月数 += v償却月数;
          } else {
            // 耐用年数経過後
            let v比較減価償却費 = v算出減価償却費;

            // 耐用年数経過年は耐用年数までの償却費と経過後の償却費を合算する
            if (v償却済月数 < v耐用月数) {
              const v耐用残月数 = v耐用月数 - v償却済月数;
              v償却済月数 += v耐用残月数;
              v比較減価償却費 =
                monthlyConvert(v算出減価償却費, v耐用残月数) +
                getMinCompareTo5pct(monthlyConvert(v算出減価償却費, 12 - v耐用残月数));
            }
            before95pct減価償却費 = getMinCompareTo5pct(v比較減価償却費);
          }
          // 計算した償却費が0の時はループを抜ける
          if (before95pct減価償却費 <= 0) break;

          push減価償却AndReduce期末簿価(before95pct減価償却費);
        }

        // （イ）償却累計額が取得価額×95%となった後
        // 　　減価償却費＝（取得価額×5%ー１）×月数／60
        // 　　※(イ)の計算式内の月数＝12。（当システムにおいては常に12。期中で売却や除却した場合は月数≠12となるが、そのようなケースは手入力で対応）
        const after95pct減価償却費 = this.roundBigNumberToInteger(
          new BigNumber(v取得価額5pct - CALC_CONST.残存価額.有形).times(12).div(60)
        );
        if (after95pct減価償却費 > 0) {
          while (v期末簿価 > CALC_CONST.残存価額.有形) {
            push減価償却AndReduce期末簿価(
              after95pct減価償却費 < v期末簿価 ? after95pct減価償却費 : v期末簿価 - CALC_CONST.残存価額.有形
            );
          }
        } else {
          // 計算した償却費が0の時は簿価1円を残して償却終了
          push残存価額(CALC_CONST.残存価額.有形);
        }

        break;

      case E償却方法.定率法250pct:
      case E償却方法.定率法200pct:
        // 償却率が取得できない場合は計算しない
        if (v計算用償却率 === undefined) break;

        const v定率法償却率 = v計算用償却率.償却率;

        // 償却率が1の時は、｢改定償却率｣｢保証率｣は使用せず、償却開始年(月割りがあれば次年)に簿価1円を残して償却終了
        if (v定率法償却率 === 1) {
          // 初年度の使用月数が12カ月でなければ月割りで償却
          if (v初年度使用月数 !== 12 && v経過年数 === 0)
            push減価償却AndReduce期末簿価(get初年度換算減価償却費(item.取得価額 * v定率法償却率));

          // 簿価1円を残して償却終了
          push残存価額(CALC_CONST.残存価額.有形);
          break;
        }

        // 償却率が1以外の時
        // （ア）毎期の減価償却費が償却保証額以上の時
        // 減価償却費＝未償却残高（取得価額ー償却累計額）×定率法（200% or 250%）償却率
        // ※償却保証額：固定資産の取得価額×その固定資産の保証率
        const 保証率 = v計算用償却率.保証率!;
        const 償却保証額 = multiplyAndRound(item.取得価額, 保証率);
        let v定率法減価償却費 = multiplyAndRound(v期末簿価, v定率法償却率);
        while (v定率法減価償却費 > 0 && 償却保証額 <= v定率法減価償却費) {
          const 計算用定率法減価償却費 = get初年度換算減価償却費(v定率法減価償却費);
          push減価償却AndReduce期末簿価(計算用定率法減価償却費);
          v定率法減価償却費 = multiplyAndRound(v期末簿価, v定率法償却率);
        }
        // （イ）毎期の減価償却費が償却保証額未満となった時以降
        // 減価償却費＝改定取得価額（毎期の減価償却費＜償却保証額となった期の前期期末簿価）×改定償却率
        // ※改定取得価額・・・毎期計算する減価償却費が初めて償却保証額を下回る場合（減価償却費＜償却保証額）の、初めて下回る事業年度の期首簿価（=前期末簿価）
        const 改定償却率 = v計算用償却率.改定償却率!;
        const 改定取得価額 = v期末簿価;
        v定率法減価償却費 = multiplyAndRound(改定取得価額, 改定償却率);
        while (v定率法減価償却費 > 0 && v定率法減価償却費 < v期末簿価) {
          push減価償却AndReduce期末簿価(v定率法減価償却費);
        }
        push残存価額(CALC_CONST.残存価額.有形);

        // 一覧表示用に定率法償却をセット
        定率法償却 = {
          保証率,
          改定償却率,
          償却保証額,
          改定取得価額,
        };

        break;

      case E償却方法.リース期間定額法:
        // ※残存価額は0円（0円まで償却する）
        // 減価償却費＝（取得価額ー残価保証額）×その年のリース期間の月数／リース期間の月数
        const vリース月数 = item.リース償却!.月数;
        const v残価保証額 = item.リース償却!.残価保証額 ?? 0;

        let v残り月数 = vリース月数;
        while (v残り月数 > 0) {
          const 償却月数 = v経過年数 === 0 ? v初年度使用月数 : Math.min(v残り月数, 12);
          push減価償却AndReduce期末簿価(
            Math.min(
              this.calcPercentByBigNumberWith減価償却費端数処理(item.取得価額 - v残価保証額, 償却月数, vリース月数),
              v期末簿価 - v残価保証額
            )
          );
          v残り月数 -= 償却月数;
        }
        push残存価額(v残価保証額);
        break;

      case E償却方法.一括償却資産:
        // ※残存価額は0円（0円まで償却する）
        // 減価償却費＝取得価額÷3
        const 一括償却資産減価償却費 = this.roundBigNumberToInteger(
          new BigNumber(item.取得価額).div(CALC_CONST.一括償却資産分割回数)
        );
        for (let i = 0; i < CALC_CONST.一括償却資産分割回数; i++) {
          if (v期末簿価 < 一括償却資産減価償却費) break;
          push減価償却AndReduce期末簿価(一括償却資産減価償却費);
        }
        push残存価額(CALC_CONST.残存価額.無形);
        break;

      case E償却方法.任意償却:
        // ユーザー自身が全て登録・計算する。
        // 何もしない
        break;
    }

    // 償却決算年toまでは減価償却費0円で値をセットする
    for (let y = v償却開始年 + v経過年数; y <= this.償却決算年to; y++) {
      push減価償却AndReduce期末簿価(0);
    }

    return {
      減価償却s,
      定率法償却,
    };
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 機能種別を取得します(機能種別に存在しない場合は例外発生)
   *
   * @param x
   * @returns
   */
  private getFunctionType(x: string | number): 償却FunctionType {
    if (償却FunctionTypeKeys.some((key) => key === x)) return x as 償却FunctionType;
    throw new Error(`Unexpected values: ${x}`);
  }

  /**
   * BigNumberをc減価償却費端数処理による端数処理をして、整数を返却します
   *
   * @param x
   * @returns
   */
  private roundBigNumberToInteger(x: BigNumber): number {
    return x.integerValue(this.bnRoundingMode.減価償却).toNumber();
  }

  /**
   * valueを引数の割合で換算し、事業所.c減価償却費端数処理で端数処理した値を返します
   *
   * @param value
   * @param numerator
   * @param denominator
   * @returns
   */
  private calcPercentByBigNumberWith減価償却費端数処理(value: number, numerator: number, denominator: number): number {
    return this.calcPercentByBigNumber(value, numerator, denominator, this.bnRoundingMode.減価償却);
  }

  /**
   * 年月から年・月のオブジェクトを返却します
   *
   * @param yyyymm
   * @returns
   */
  private getYm(yyyymm: number): YearMonth {
    const year = Math.floor(yyyymm / 100);
    return {
      year,
      month: yyyymm % year,
    };
  }

  /**
   * 引数の月が決算月より前ならその年を、後であれば1年後の年を返却します
   *
   * @param ym
   * @returns
   */
  private getYearBy決算月(ym: YearMonth): number {
    return ym.month <= this.c計画0期の決算期Ym.month ? ym.year : ym.year + 1;
  }

  /**
   * 償却率表から計算用償却率を取得します
   *
   * @param item
   * @returns
   */
  private get計算用償却率(item: 償却EditItem): 計算用償却率 | undefined {
    const v償却率 = m償却率.find((m償却率item) => {
      // 定額法（無形固定資産）は、定額法と同じ償却率を使用する
      const v償却方法 = item.償却方法 === E償却方法.定額法無形固定資産 ? E償却方法.定額法 : item.償却方法;
      return m償却率item.償却方法 === v償却方法;
    });
    if (v償却率 === undefined) return v償却率;
    return v償却率.計算用償却率s.find((v計算用償却率) => v計算用償却率.耐用年数 === item.耐用年数);
  }

  /**
   * 計画フェーズの時、対象年が計画0期の前期の時は前期末簿価を、それ以外は引数の値を返却します
   *
   * @param year
   * @param value
   * @param v前期末簿価
   * @returns
   */
  private replace期末簿価if計画0期前期(year: number, value: number, v前期末簿価?: number): number {
    if (this.eフェーズ種別 === Eフェーズ種別.分析フェーズ || v前期末簿価 === undefined) return value;
    return year === this.c計画0期の決算期Ym.year - 1 ? v前期末簿価 : value;
  }
}
